import { appendReplayCustom, initSessionReplay, setReplayUser } from '@jobtarget/browser-rum';
import { getDecodedToken } from './tokenHelper';

/**
 * JobTarget session replay. CloudWatch RUM keeps performance, errors and traces.
 *
 * This app used to run two other recorders: CloudWatch RUM's RRWebPlugin and the
 * Statsig session replay plugin. Both are gone. Two DOM recorders in one document
 * both patch fetch, XHR and console, and each pays for the same session.
 */
export function initReplay(): void {
  initSessionReplay({
    service: 'interview',
    env: import.meta.env.VITE_RUM_ENV || 'sb',
    version: import.meta.env.VITE_APP_RELEASE,
    // npm has no script tag to infer the intake from; prod intake, prod console.
    ingestOrigin: 'https://rum-replay-intake.jobtarget.com',
    // Replays show what was typed. Passwords, .sr-mask and fields whose name reads
    // as a secret (ssn, card, cvv, pin, token...) stay masked at every level;
    // ordinary fields do not.
    privacy: 'allow',
    sessionReplaySampleRate: 100,
    // The screener runs INSIDE an iframe: jobseeker's /jt-screener embeds
    // <interview>/v1/interview/onboarding/<guid>. The SDK disables itself in a
    // frame by default, on the assumption that the parent page records it —
    // and that assumption does not hold here. The parent is a different origin
    // (rrweb only reaches same-origin frames), it runs no rrweb at all (jobseeker
    // is Flutter), and its recorder is screenshot-less besides (captureFrames:
    // false). Without this the entire candidate screener journey recorded
    // nowhere. The guard's real target — one page of an app embedding another
    // page of the SAME app, giving two recorders and phantom view loads — is not
    // this case.
    recordInIframe: true,
  });
  bindReplayUser();
  noteApplicantGuid();
}

/**
 * Name the recruiter on the session.
 *
 * This app has two audiences and only one of them is anonymous. The applicant
 * screener has no token, so getDecodedToken() returns null and those sessions
 * stay Anonymous, which is correct. The recruiter side is behind ProtectedRoute
 * and carries a jtToken whose claims already drive the rest of the app, so there
 * was never a reason for those sessions to be anonymous too.
 *
 * Safe this early: the SDK holds the identity until the mint returns, then sends
 * it once per session. The token is readable synchronously here — getTokenFromCookie
 * falls back to the URL parameter, which is how the iframe embed receives it.
 */
export function bindReplayUser(): void {
  const claims = getDecodedToken();
  if (!claims) return;
  void setReplayUser(claims.userId, claims.email, { companyId: claims.companyId });
}

/** The applicant guid as it appears in the three routes that carry one. */
const APPLICANT_GUID_RE =
  /\/(?:interview\/onboarding|recruiter\/interview)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * Record which applicants this session is about, as `custom.applicantGuid`.
 *
 * It is the JOIN KEY for a candidate's journey. jobseeker frames this app and
 * passes the same value — its /jt-screener?token=<guid> becomes our
 * /v1/interview/onboarding/<guid> — but the two recorders cannot share a session:
 * jobseeker's Flutter SDK keeps its session in localStorage, which is origin
 * scoped, while ours is a cookie on jobtarget.com. So one journey is always two
 * sessions, and this attribute is what finds both of them.
 *
 * The value is a LIST, because a session can legitimately open several applicants
 * and the ingest stores one value per key: picking one of them would leave the
 * others unfindable, and picking the LAST would mislabel the session outright.
 * The console filters this key on a part of the value rather than the whole, so
 * pasting any one guid finds the session.
 *
 * appendReplayCustom (SDK 1.9.0) owns the list: it dedupes, it holds the value
 * until the session exists, and it keeps it in localStorage keyed by the session
 * id — so a second tab, a reload, or jobseeker re-pointing the iframe EXTENDS the
 * list rather than replacing it. Accumulating here instead could not do that,
 * because module state dies with the document while the session outlives it.
 */
export function noteApplicantGuid(pathname: string = window.location.pathname): void {
  const match = APPLICANT_GUID_RE.exec(pathname);
  if (!match) return;
  void appendReplayCustom('applicantGuid', match[1].toLowerCase());
}
